import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Button } from "@mui/material";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "src/redux/store";
import { toast } from "react-toastify";
import { KTIcon } from "src/marketConnect/helpers";
import TableLoadingShell from "src/utils/TableLoadingShell";
import {
    getCustomFieldsListAsync,
    deleteCustomFieldsAsync,
} from "src/redux/actions/omsPageActions";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";
import { formatDateLocal, formatTimeZone } from "src/utils/TimeZoneDateFormat";
import CreateEditCustomFieldModal from "./CreateEditCustomFields";
import { DATA_TYPES, OWNER_OPTIONS } from "src/utils/constants";
import FilterDropdown from "src/marketConnect/helpers/components/FilterDropdown";

const CustomFieldsListing: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();

    const [searchString, setSearchString] = useState("");
    const [paginationData, setPaginationData] = useState({
        page_num: 1,
        page_limit: 10,
        search: "",
        sort_order: "",
        sort_by: "",
        isActive: undefined as boolean | undefined,
        owner: "",
    });

    const [filters, setFilters] = useState<Record<string, any>>({});
    const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);

    const [showModal, setShowModal] = useState(false);
    const [editData, setEditData] = useState<any>(null);

    // delete
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedField, setSelectedField] = useState<any>(null);

    const statusKey = "isActive";
    const filterOptions = ["ACTIVE", "INACTIVE"];
    const ownerKey = "owner";
    const inputTypeKey = "input_type";

    const { customFieldsList, pagination, loading } = useSelector(
        (state: any) => state.omsCustomFieldsSlice
    );

    const filteredCustomFieldsList = useMemo(() => {
        let data = customFieldsList || [];

        if (filters[inputTypeKey]) {
            data = data.filter((item: any) => item?.input_type === filters[inputTypeKey]
            );
        }

        return data;
    }, [customFieldsList, filters]);

    // ================= FETCH =================
    const fetchCustomFields = useCallback(() => {
        dispatch(getCustomFieldsListAsync({
            paramData: {
                page: paginationData.page_num, limit: paginationData.page_limit, search: paginationData.search,
                isActive: paginationData.isActive, sort_by: paginationData.sort_by, sort_order: paginationData.sort_order, owner: paginationData.owner || undefined,
            },
        })).unwrap()
            .catch((err) => {
                toast.error(err?.data?.message || "Failed to fetch custom fields");
            });
    }, [paginationData, dispatch]);

    useEffect(() => {
        fetchCustomFields();
    }, [fetchCustomFields]);

    // ================= FILTER =================
    useEffect(() => {
        setPaginationData((prev) => ({
            ...prev,
            isActive:
                filters[statusKey] === "ACTIVE"
                    ? true
                    : filters[statusKey] === "INACTIVE"
                        ? false
                        : undefined,
            owner: filters[ownerKey]?.toLowerCase() || "",
            page_num: 1,
        }));
    }, [filters]);

    const handleResetFilters = () => {
        setFilters({});
        setSearchString("");
        setPaginationData({
            page_num: 1,
            page_limit: 10,
            search: "",
            isActive: undefined,
            sort_by: "created_at",
            sort_order: "desc",
            owner: "",
        });
    };

    // ================= DELETE =================
    const handleDelete = (row: any) => {
        setSelectedField(row);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        if (!selectedField) return;

        dispatch(deleteCustomFieldsAsync({ id: selectedField.id }))
            .then((res: any) => {
                console.log("deleyte res", res);
                if (res?.payload?.status === 200) {
                    toast.success(res?.payload?.message || "Custom field deleted successfully");
                } else {
                    toast.error(res?.payload?.data?.message || "Failed to delete custom field");
                }
                fetchCustomFields();
            })
            .catch(() => {
                toast.error("Failed to delete custom field");
            })
            .finally(() => {
                setShowDeleteModal(false);
                setSelectedField(null);
            });
    };

    // ================= COLUMNS =================
    const columns: TableColumn<any>[] = [
        {
            name: "Field Name",
            minWidth: "200px",
            center: true,
            cell: (row) => {
                const text = row?.display_name || "-";

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-${row?.id}`}>{text}</Tooltip>}
                    >
                        <span
                            className="text-truncate"
                            style={{
                                maxWidth: "180px",
                                display: "inline-block",
                                cursor: "pointer",
                            }}
                        >
                            {text}
                        </span>
                    </OverlayTrigger>
                );
            },
        },
        {
            name: (
                <>
                    <span>Input Type</span>
                    <FilterDropdown
                        id={`filter-${inputTypeKey}`}
                        filters={filters}
                        setFilters={setFilters}
                        openFilterKey={openFilterKey}
                        setOpenFilterKey={setOpenFilterKey}
                        hasActiveFilter={!!filters[inputTypeKey]}
                        options={[
                            {
                                label: "Filter Input Type",
                                key: inputTypeKey,
                                values: DATA_TYPES,
                            },
                        ]}
                    />
                </>
            ),
            selector: (row) => row?.input_type || "-",
            minWidth: "180px",
            center: true,
        },
        {
            name: (
                <>
                    <span>Owner</span>
                    <FilterDropdown
                        id={`filter-${ownerKey}`}
                        filters={filters}
                        setFilters={setFilters}
                        openFilterKey={openFilterKey}
                        setOpenFilterKey={setOpenFilterKey}
                        hasActiveFilter={!!paginationData.owner}
                        options={[
                            {
                                label: "Filter Owner",
                                key: ownerKey,
                                values: OWNER_OPTIONS.map((item) => item.value),
                            },
                        ]}
                    />
                </>
            ),
            selector: (row) => row?.owner || "-",
            minWidth: "180px",
            center: true,
        },
        {
            name: "Mandatory",
            selector: (row) => (row?.isRequired ? "Yes" : "No"),
            minWidth: "120px",
            center: true,

        },
        {
            name: "Help Text",
            minWidth: "200px",
            center: true,
            cell: (row) => {
                const text = row?.description || "-";

                return (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`tooltip-${row?.id}`}>{text}</Tooltip>}
                    >
                        <span
                            className="text-truncate"
                            style={{
                                maxWidth: "180px",
                                display: "inline-block",
                                cursor: "pointer",
                            }}
                        >
                            {text}
                        </span>
                    </OverlayTrigger>
                );
            },
        },
        {
            name: (
                <>
                    <span>Status</span>
                    <FilterDropdown
                        id={`filter-${statusKey}`}
                        filters={filters}
                        setFilters={setFilters}
                        openFilterKey={openFilterKey}
                        setOpenFilterKey={setOpenFilterKey}
                        hasActiveFilter={paginationData.isActive !== undefined}
                        options={[
                            {
                                label: "Filter Status",
                                key: statusKey,
                                values: filterOptions,
                            },
                        ]}
                    />
                </>
            ),
            minWidth: "160px",
            center: true,
            cell: (row) => (
                <span
                    className={`badge text-capitalize px-3 py-2 rounded-pill bg-${row?.isActive ? "success" : "secondary"
                        }`}
                >
                    {row?.isActive ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Created At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <i className="fa fa-info-circle" />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            center: true,
            width: "180px",
            cell: (row) => (row?.created_at ? formatDateLocal(row.created_at) : ""),
        },
        {
            name: (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Updated At</span>
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{formatTimeZone()}</Tooltip>}
                    >
                        <span style={{ cursor: "pointer", color: "darkgray" }}>
                            <i className="fa fa-info-circle" />
                        </span>
                    </OverlayTrigger>
                </div>
            ),
            center: true,
            width: "180px",
            cell: (row) => (row?.updated_at ? formatDateLocal(row.updated_at) : "-"),
        },
        {
            name: "Action",
            maxWidth: "140px",
            center: true,
            cell: (row) => (
                <div className="abd-actions d-flex align-items-center gap-2">
                    <button
                        className="btn btn-icon btn-bg-light btn-color-primary btn-sm mt-1"
                        onClick={() => {
                            setEditData(row);
                            setShowModal(true);
                        }}
                    >
                        <KTIcon iconName="pencil" className="fs-4" />
                    </button>

                    <button
                        className="btn btn-icon btn-bg-light btn-color-danger btn-sm mt-1"
                        onClick={() => handleDelete(row)}
                    >
                        <KTIcon iconName="trash" className="fs-4" />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            button: true,
        },
    ];

    return (
        <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <span className="fw-bold text-lg" style={{ fontSize: "16px" }}>
                    Custom Fields
                </span>
            </div>

            <div className="card card-flush">
                <div className="card-header d-flex justify-content-between align-items-center">
                    {/* SEARCH - SAME AS REF */}
                    <div className="position-relative">
                        {searchString === "" && (
                            <i className="bi bi-arrow-return-left position-absolute top-50 translate-middle-y end-0 me-2 text-secondary"></i>
                        )}
                        <input
                            className="form-control mr-sm-2 input-sm"
                            type="search"
                            placeholder="Search"
                            value={searchString}
                            onChange={(e) => setSearchString(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    setPaginationData((prev) => ({
                                        ...prev,
                                        search: searchString,
                                        page_num: 1,
                                    }));
                                }
                            }}
                        />
                    </div>

                    {/* ACTIONS - SAME AS REF */}
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-sm btn-primary w-100px fw-bold py-2"
                            onClick={handleResetFilters}
                        >
                            Reset Filters
                        </button>

                        <button
                            className="btn btn-sm btn-primary fw-bold py-2"
                            onClick={() => {
                                setEditData(null);
                                setShowModal(true);
                            }}
                        >
                            + Create New Custom Field
                        </button>

                    </div>
                </div>

                <TableLoadingShell loading={loading}>
                <DataTable
                    fixedHeader
                    persistTableHead
                    data={filteredCustomFieldsList || []}
                    columns={columns}
                    pagination
                    paginationServer
                    paginationTotalRows={pagination?.count || 0}
                    paginationPerPage={paginationData.page_limit}
                    onChangePage={(page) =>
                        setPaginationData((prev) => ({ ...prev, page_num: page }))
                    }
                    onChangeRowsPerPage={(limit) =>
                        setPaginationData((prev) => ({
                            ...prev,
                            page_limit: limit,
                            page_num: 1,
                        }))
                    }
                    progressPending={false}
                    noDataComponent={<div className="text-secondary"><h5>No records found</h5></div>}
                    customStyles={{
                        table: { style: { height: "55vh" } },
                        headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
                        cells: {
                            style: {
                                padding: "10px",
                                borderBottom: "1px solid gainsboro",
                            },
                        },
                    }}
                />
                </TableLoadingShell>
            </div>
            {/* Create Edit Popup */}
            <CreateEditCustomFieldModal
                show={showModal}
                editData={editData}
                onClose={() => {
                    setShowModal(false);
                    setEditData(null);
                }}
                onSuccess={() => {
                    setShowModal(false);
                    setEditData(null);
                    fetchCustomFields(); // LIST API CALLED HERE
                }}
            />


            {/* DELETE CONFIRMATION */}
            <CommonAlertModal
                title="Are you sure to delete custom field?"
                subtitle="Custom field data will be deleted"
                isVisible={showDeleteModal}
                isTermsVisible={false}
                onSuccess={handleDeleteConfirm}
                onCancel={() => setShowDeleteModal(false)}
                successBtnTitle="Yes, Delete"
                cancelBtnTitle="Cancel"
                successBtnClasses="btn btn-sm btn-danger m-2"
            />
        </>
    );
};

export default CustomFieldsListing;
